import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Service } from "rainbow-web-sdk/services/service";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { DEVICE_MANAGEMENT_SVC, DeviceManagementServiceRB } from '../deviceManagement/deviceManagement.service';
import { WEBRTC_CONFIG_UTILS_SVC, WebrtcConfigAndUtils } from '../webrtc/webrtcConfigAndUtils';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { ContactService } from '../core/contact.service';

export const HORIZON_SVC = 'HorizonService';

export class HorizonService extends Service {
    private logger: LoggerService = LoggerService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB;
    private webrtcConfigAndUtils: WebrtcConfigAndUtils;
    private centralizedService;

    public started: boolean = false;
    public sdkInitialized: boolean = false;
    public vdiClientConnected: boolean = false;
    public horizonSDK;

    private apiOverwrited: boolean = false;

    private nativeAPIs = {};
    public windowRef: string;

    public static getInstance() {
        return Service.create(HORIZON_SVC, HorizonService);
    }

    public static build() {
        return new HorizonService();
    }

    protected constructor() {
        super();
    }

    public async start() {
        if (this.xmppService.connected) { this.initHorizonSDK(); }
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); });
    }

    public stop() {
        if (this.started) {
            this.logger.log('[HorizonService] stop');
            this.resetGlobalAPIs();
            this.started = false;
            this.vdiClientConnected = false;
        }
    }

    private initHorizonSDK() {
        if (this.started) return;
        //check if user have right to use video before activating citrix

        this.started = true;
        this.logger.log('[HorizonService] start -- initHorizonSDK');

        this.horizonSDK = window["HorizonWebRtcRedirectionAPI"];
        if (this.horizonSDK) {
            //try to init the horizon SDK
            this.setWindowAPIs();
            this.sdkInitialized = this.horizonSDK.initSDK(this.logger, "Rainbow For VMWare Horiwon", (event) => {
                try {
                    this.logger.info(`[HorizonService] event received ${event?.event}`);
                    switch (event?.event) {
                        case "vdiClientConnected":
                            //add some timeout before starting, as in case of error, it connects and then disconnects ...
                            this.vdiClientConnected = true;
                            setTimeout(() => {
                                if (this.vdiClientConnected) { this.overwriteGlobalAPIs(); this.updatePopoutConfiguration(); }
                            }, 250);
                            break;
                        case "vdiClientDisconnected":
                            this.vdiClientConnected = false;
                            break;
                        default:
                        //do nothing
                    }
                }
                catch (error) {
                    this.logger.error(`[HorizonService] event received error -- ${error}`);
                }
            });

            this.logger.log(`[HorizonService] sdk initialized -- ${this.sdkInitialized}`);
        }
    }

    private setWindowAPIs() {
        this.logger.log('[HorizonService] start -- setWindowAPIs');
        this.centralizedService = this.mainService.inject("CentralizedService");
        window["getHorizonClientID"] = this.centralizedService.system.vmWareHorizon.getHorizonClientID;
        window["getHorizonWSSPort"] = this.centralizedService.system.vmWareHorizon.getHorizonWSSPort;
        window["getWindowReference"] = this.centralizedService.system.vmWareHorizon.getWindowReference;
    }

    private overwriteGlobalAPIs() {
        if (this.apiOverwrited) {
            this.logger.log('[HorizonService] overwriteGlobalAPIs already set'); return;
        }

        this.logger.log('[HorizonService] start -- overwriteGlobalAPIs');
        this.apiOverwrited = true;
        this.deviceManagementService = this.mainService.inject(DEVICE_MANAGEMENT_SVC) as DeviceManagementServiceRB;
        this.webrtcConfigAndUtils = this.mainService.inject(WEBRTC_CONFIG_UTILS_SVC) as WebrtcConfigAndUtils;

        navigator.mediaDevices["enumerateDevices2"] = navigator.mediaDevices.enumerateDevices;
        navigator.mediaDevices.enumerateDevices = this.horizonSDK.enumerateDevices;

        //overwrite the set sink id API
        this.nativeAPIs["setSpeakerForAudioElement"] = this.deviceManagementService.setSpeakerForAudioElement;

        //map the global audio tag
        this.horizonSDK.onAudioCreated(document.getElementById("globalAudioTag"));

        this.deviceManagementService.setSpeakerForAudioElement = async (element, device) => {
            //find the same device from the original list of devices and select it
            const devices = await navigator.mediaDevices["enumerateDevices2"]();
            let deviceToUse;
            let backupDeviceToUse;
            let labelToFind;
            if (device.label.indexOf("(") !== -1) {
                labelToFind = device.label.substring(device.label.indexOf("(") + 1, device.label.lastIndexOf(")"));
            }

            devices.forEach((dev) => {
                if (dev.kind === device.kind) {
                    if (device.label.indexOf(dev.label) !== -1 || dev.label.indexOf(device.label) !== -1) {
                        deviceToUse = dev;
                    }
                    if (labelToFind && dev.label.indexOf(labelToFind) !== -1) {
                        backupDeviceToUse = dev;
                    }
                }
            });

            if (!deviceToUse && backupDeviceToUse) {
                //we could not find device, try to find via smaller label only
                deviceToUse = backupDeviceToUse;
            }

            await this.nativeAPIs["setSpeakerForAudioElement"].bind(this)(element, deviceToUse);
        };

        this.nativeAPIs["getUserMedia"] = navigator.mediaDevices.getUserMedia;
        navigator.mediaDevices.getUserMedia = async (constraints: any) => {

            //remove the exact thing
            if (constraints.audio?.deviceId?.exact) {
                if (constraints.audio?.deviceId?.exact === "default") { constraints.audio = true; }
                else { constraints.audio.deviceId = constraints.audio?.deviceId?.exact; }
            }


            if (constraints.video?.deviceId?.exact) {
                if (constraints.video?.deviceId?.exact === "default") { delete constraints.video.deviceId; }
            }

            if (constraints.video?.mandatory?.chromeMediaSourceId) {
                const screenInfos = await this.horizonSDK.getScreensInfo();
                let index = 0;
                if (screenInfos.length > 1) {
                    //try to find the right screen but ...
                    if (constraints.video?.mandatory?.chromeMediaSourceId.indexOf("0:0") === -1) {
                        index = 1;
                    }
                }
                await this.horizonSDK.onScreenSelected(screenInfos[index].id);
                constraints = {
                    video: {
                        width: { max: 1920 },
                        height: {max: 1080 },
                        frameRate: 10
                    }
                };
                this.logger.log(`[HorizonService] getUserMedia -- ${JSON.stringify(constraints)}`);
                return this.horizonSDK.getDisplayMedia(constraints);
            }

            this.logger.log(`[HorizonService] getUserMedia -- ${JSON.stringify(constraints)}`);
            return this.horizonSDK.getUserMedia(constraints);
        };


        this.deviceManagementService.checkDeviceList();

        this.nativeAPIs["RTCPeerConnection"] = window.RTCPeerConnection;
        this.nativeAPIs["MediaStream"] = window.MediaStream;
        window.MediaStream = this.horizonSDK.newMediaStream;
        //@ts-ignore
        window.RTCPeerConnection = function(config, constraints) { return window["HorizonWebRtcRedirectionAPI"].newPeerConnection(config, constraints); };

        this.nativeAPIs["attachMediaStream"] = this.webrtcConfigAndUtils.RTC.attachMediaStream;
        this.nativeAPIs["clearMediaStream"] = this.webrtcConfigAndUtils.RTC.clearMediaStream;
        this.webrtcConfigAndUtils.RTC.attachMediaStream = async (element, stream) => {
            if (element[0]?.nodeName === "VIDEO") {
                this.windowRef = await window["getWindowReference"]();
                this.horizonSDK.onVideoCreated(element[0], this.windowRef ? this.windowRef : "");
            }
            else { this.horizonSDK.onAudioCreated(element[0]); }
            this.nativeAPIs["attachMediaStream"](element, stream);
            element[0].play();
        };
        this.webrtcConfigAndUtils.RTC.clearMediaStream = (element) => { this.nativeAPIs["clearMediaStream"](element); };

        this.settingsService.setSetting("horizonConnected", "true");
        this.xmppService.connection.jingle.ice_config["horizon"] = true;

        //disable pop-out in case of VDI connected
        this.settingsService.setSetting("openPopoutOnMinimize", "false");
        this.settingsService.setSetting("openPopoutOnAnswerCall", "false");
        this.settingsService.setSetting("endCallOnClosePopout", "false");
    }

    private resetGlobalAPIs(disconnected: boolean = false) {
        this.logger.log('[HorizonService] stop -- resetGlobalAPIs');
        this.deviceManagementService = this.mainService.inject(DEVICE_MANAGEMENT_SVC) as DeviceManagementServiceRB; ;
        this.webrtcConfigAndUtils = this.mainService.inject(WEBRTC_CONFIG_UTILS_SVC) as WebrtcConfigAndUtils;

        this.apiOverwrited = false;

        navigator.mediaDevices.getUserMedia = this.nativeAPIs["getUserMedia"];
        navigator.mediaDevices.enumerateDevices = navigator.mediaDevices["enumerateDevices2"];
        this.deviceManagementService.setSpeakerForAudioElement = this.nativeAPIs["setSpeakerForAudioElement"];

        window.RTCPeerConnection = this.nativeAPIs["RTCPeerConnection"];
        window.MediaStream = this.nativeAPIs["MediaStream"];
        this.webrtcConfigAndUtils.RTC.clearMediaStream = this.nativeAPIs["clearMediaStream"];
        this.webrtcConfigAndUtils.RTC.attachMediaStream = this.nativeAPIs["attachMediaStream"];

        this.deviceManagementService.setSpeakerForAudioElement = this.nativeAPIs["setSpeakerForAudioElement"];

        this.settingsService.setSetting("horizonConnected", "false");
        this.xmppService.connection.jingle.ice_config["horizon"] = false;

        // //enable DTLS SRTP for citrix env
        // this.xmppService.connection.jingle.ice_config["enableDtlsSrtp"] = false;
        // this.xmppService.connection.jingle.media_constraints = {};

        if (disconnected) {
            //reset the list of devices
            this.deviceManagementService.checkDeviceList();
        }
    }

    private async onConnectionStateChangeEvent(event: RBEvent) {
        if (event.name === 'ON_XMPP_DISCONNECTED') {
            if (event.data.reason === "LOGOUT") {
                this.stop();
            }
            else {
                this.logger.log('[HorizonService] onConnectionStateChangeEvent : disconnected');
            }
        }
        else if (event.name === 'ON_XMPP_CONNECTED') {
            this.logger.log('[HorizonService] onConnectionStateChangeEvent : connected');
            if (!this.started) {
                await this.initHorizonSDK();
            }
            if (event.data.reason === 'RECONNECTION' && this.started) {
                //try to reconnect the SDK
                this.horizonSDK.onWindowSessionConnected(true);
            }

            this.updatePopoutConfiguration();
        }
    }

    private updatePopoutConfiguration() {
        if (this.vdiClientConnected) {
            //check if we need to disable pop-out
            if (this.contactService?.userContact?.webrtcVideoEnabled || this.contactService?.userContact?.webrtcSharingEnabled) {
                //disable pop-out in case of VDI connected
                this.settingsService.setSetting("openPopoutOnMinimize", "false");
                this.settingsService.setSetting("openPopoutOnAnswerCall", "false");
                this.settingsService.setSetting("endCallOnClosePopout", "false");
            }
        }
    }
}
